import { useEffect, useMemo, useState } from "react";
import { useAuth, apiClient } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from "./ui/dialog";
import { toast } from "sonner";
import { Crown, Check, X, Sparkles, ArrowDownCircle, Rocket, Leaf, ChevronDown, ChevronUp } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

// Fallback pricing used only if /api/plans hasn't responded yet or errors.
// The Super Admin CMS at /admin/plans is the source of truth in production.
export const PLANS = [
  {
    id: "free",
    name: "Free",
    price: 0,
    description: "For solo owners just getting started",
    features: [
      { label: "Up to 3 employees", included: true },
      { label: "Time Clock kiosk + PIN", included: true },
      { label: "Schedule board", included: true },
      { label: "Sign in with email + password, Google, or magic link", included: true },
      { label: "Basic pay calculations", included: true },
    ],
  },
  {
    id: "starter",
    name: "Starter",
    price: 59,
    description: "Small teams with a single location",
    popular: true,
    features: [
      { label: "Everything in Free, plus:", included: true, section_header: true },
      { label: "Up to 20 employees", included: true },
      { label: "Auto clock-out + missed clock-out alerts", included: true },
      { label: "Email notifications", included: true },
      { label: "Shift swap workflow", included: true },
      { label: "Overtime alerts", included: true },
      { label: "Basic reports", included: true },
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: 99,
    description: "Multi-location operations & advanced control",
    features: [
      { label: "Everything in Starter, plus:", included: true, section_header: true },
      { label: "Unlimited employees", included: true },
      { label: "Announcements Board — SMS + email broadcast to your team", included: true },
      { label: "WhatsApp quick-message from employee cards", included: true },
      { label: "Phone-only sign-in via SMS OTP (for staff without email)", included: true },
      { label: "Audit Log (full 365-day history)", included: true },
      { label: "Advanced reports & trend analytics", included: true },
      { label: "Multi-location single view", included: true },
      {
        label: "Payroll sync (Gusto → QuickBooks → Square)",
        included: true,
        coming_soon: true,
        note: "+ $2/mo per payroll-enabled employee",
      },
      { label: "Labor cost forecasting", included: true, coming_soon: true },
    ],
  },
];

/**
 * Fetch plans from the Super-Admin-controlled CMS. Returns { plans, isLoading }.
 * Falls back to the static PLANS array while loading or on error so the UI
 * never renders empty.
 */
export function usePlans() {
  const [plans, setPlans] = useState(PLANS);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    apiClient.get("/plans")
      .then((res) => {
        if (cancelled) return;
        const dyn = (res.data?.plans || []).map((p) => ({
          id: p.plan_id,
          name: p.name,
          price: (p.monthly_price_cents || 0) / 100,
          annualPrice: (p.annual_price_cents || 0) / 100,
          description: p.tagline || "",
          // Heuristic: Starter is our "most popular" tier by default. Super
          // Admin can override by adding an is_popular flag later.
          popular: p.plan_id === "starter",
          features: (p.features || []).map((f) => ({
            key: f.key || f.label,
            label: f.label,
            included: !!f.included,
            coming_soon: !!f.coming_soon,
            note: f.note || "",
            section_header: !!f.section_header,
          })),
          is_free: !!p.is_free,
        }));
        if (dyn.length > 0) setPlans(dyn);
      })
      .catch(() => { /* keep fallback */ })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, []);
  return { plans, isLoading };
}

export const UpgradeBanner = () => {
  const { tenant, user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  if (!tenant || tenant.plan === "pro" || dismissed) return null;
  // Only owners can act on billing/upgrade — employees & managers don't have
  // that permission, so the banner would be misleading and noisy for them.
  if (user?.role && user.role !== "owner") return null;
  const isFree = !tenant.plan || tenant.plan === "free";
  return (
    <div className="bg-gradient-to-r from-[#0A192F] to-[#0D9488] text-white px-4 py-2 flex items-center justify-between" data-testid="upgrade-banner">
      <div className="flex items-center gap-2">
        <Crown className="w-4 h-4 text-amber-400" />
        <span className="text-sm">
          You&apos;re on the <span className="font-semibold capitalize">{tenant.plan || "free"} Plan</span>.
          {isFree ? " Upgrade to Starter to unlock kiosk clock-in, auto clock-out, shift swaps, overtime alerts, and reports." : " Upgrade to Pro for audit log, advanced reports & trend analytics, multi-location, and upcoming payroll sync."}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <UpgradeButton size="sm" variant="secondary" plan={isFree ? "starter" : "pro"} />
        <button onClick={() => setDismissed(true)} className="text-white/60 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export const UpgradeButton = ({ size = "default", variant = "default", className = "", plan = "pro", billingCycle = "monthly", compact = false, discountCode = "" }) => {
  const [loading, setLoading] = useState(false);
  const handleUpgrade = async () => {
    // Unauthenticated (landing page) — stash the intent and route to
    // sign-in. The auth-post-hydrate handler in App.js picks up
    // `pending_checkout` and immediately kicks off the Stripe session
    // so the user lands on Stripe as their first authenticated action.
    if (!localStorage.getItem("session_token")) {
      localStorage.setItem("pending_checkout", JSON.stringify({
        plan,
        billing_cycle: billingCycle,
        discount_code: discountCode || null,
        stashed_at: Date.now(),
      }));
      // Go to the landing sign-in section. We use hash `#signin` so the
      // Landing page scrolls the magic-link/Google buttons into view.
      window.location.href = "/#signin";
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.post('/subscription/checkout', {
        origin_url: window.location.origin,
        cancel_path: window.location.pathname + window.location.search,
        plan,
        billing_cycle: billingCycle,
        discount_code: discountCode || undefined,
      });
      window.location.href = res.data.url;
    } catch (err) {
      console.error("Checkout failed:", err);
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally {
      setLoading(false);
    }
  };
  const planMeta = PLANS.find(p => p.id === plan) || PLANS[1];
  const isAnnual = billingCycle === "annual";
  const monthlyEquiv = isAnnual ? +(planMeta.price * 0.8).toFixed(0) : planMeta.price;
  // Compact mode keeps the CTA readable in narrow pricing cards; the plan
  // price is already shown above the button in that context.
  const cta = compact
    ? `Upgrade to ${planMeta.name}`
    : isAnnual
      ? `Upgrade to ${planMeta.name} — $${monthlyEquiv}/mo billed yearly`
      : `Upgrade to ${planMeta.name} — $${planMeta.price}/mo`;
  return (
    <Button
      size={size}
      variant={variant}
      onClick={handleUpgrade}
      disabled={loading}
      className={`${variant === "secondary" ? "bg-white text-[#0A192F] hover:bg-slate-100" : "bg-[#0D9488] hover:bg-[#14B8A6]"} whitespace-normal break-words leading-tight ${className}`}
      data-testid={`upgrade-btn-${plan}`}
    >
      <Crown className="w-4 h-4 mr-1 shrink-0" />
      <span className="truncate max-w-full">{loading ? "Loading..." : cta}</span>
    </Button>
  );
};

export const DowngradeButton = ({ size = "default", plan, className = "" }) => {
  const { refreshTenant } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const planMeta = PLANS.find(p => p.id === plan) || PLANS[0];

  const confirmDowngrade = async () => {
    setLoading(true);
    try {
      await apiClient.post("/subscription/downgrade", { plan });
      toast.success(`Downgraded to ${planMeta.name}`);
      setOpen(false);
      if (refreshTenant) await refreshTenant();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Downgrade failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        size={size}
        variant="outline"
        onClick={() => setOpen(true)}
        className={`border-slate-300 text-slate-600 hover:bg-slate-50 ${className}`}
        data-testid={`downgrade-btn-${plan}`}
      >
        <ArrowDownCircle className="w-4 h-4 mr-1" />
        Downgrade to {planMeta.name}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Downgrade to {planMeta.name}?</DialogTitle>
            <DialogDescription>
              You&apos;ll lose access to features in your current plan immediately. You can upgrade again any time.
              No refunds are issued for the unused portion of your current billing period.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              className="bg-slate-700 hover:bg-slate-800 text-white"
              onClick={confirmDowngrade}
              disabled={loading}
              data-testid={`confirm-downgrade-${plan}`}
            >
              {loading ? "Downgrading…" : `Downgrade to ${planMeta.name}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const PlanBadge = () => {
  const { tenant } = useAuth();
  if (!tenant) return null;
  const plan = tenant.plan || "free";
  if (plan === "pro") return <Badge className="bg-amber-100 text-amber-700"><Crown className="w-3 h-3 mr-1" /> Pro</Badge>;
  if (plan === "starter") return <Badge className="bg-blue-100 text-blue-700"><Sparkles className="w-3 h-3 mr-1" /> Starter</Badge>;
  return <Badge variant="outline" className="text-slate-500">Free</Badge>;
};

// Reusable pricing grid (used in Settings + Landing). When `currentPlan` is supplied,
// we highlight the active plan and CTA reflects the user's current tier.
export const PricingGrid = ({ currentPlan = null, onSelectFree = null, ctaSize = "default" }) => {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const isAnnual = billingCycle === "annual";
  const { plans, isLoading } = usePlans();

  // Discount code UX — one input at the top of the grid. On success we hold
  // {code, applicable_plans, discount_type, discount_value, billing_cycle}
  // so the price display on each card can preview the discount too. The
  // validated code is forwarded to UpgradeButton → /api/subscription/checkout.
  const [codeInput, setCodeInput] = useState("");
  const [codeValidating, setCodeValidating] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [appliedCode, setAppliedCode] = useState(null); // { code, discount_type, discount_value, applicable_plans, billing_cycle, savings_cents }

  // Track which plan cards have their "See all features" expanded. We cap
  // the tallest tier (Pro) to the row count of its shorter siblings so all
  // three cards render at the same height; the toggle reveals the rest.
  const [expandedPlans, setExpandedPlans] = useState(new Set());
  const togglePlanExpanded = (planId) => {
    setExpandedPlans((prev) => {
      const next = new Set(prev);
      if (next.has(planId)) next.delete(planId);
      else next.add(planId);
      return next;
    });
  };

  // -----------------------------------------------------------------------
  // Build a *canonical* feature list — the ordered union of every feature
  // (keyed by `key`, not `label`, so different-worded variants of the same
  // feature merge into one row, e.g. "Audit Log" vs "Audit Log (365-day)").
  // Merge order: free → starter → pro, first-seen key wins. When multiple
  // plans use different labels for the same key, we prefer the LONGEST /
  // most descriptive one so the row communicates what the user actually
  // unlocks by upgrading.
  //
  // We also collapse "umbrella" keys (e.g. `everything_starter` on the Pro
  // plan) — these mean "this tier inherits every feature from the lower
  // tier" and shouldn't render as their own row. We resolve inheritance
  // by injecting the umbrella'd features into `featuresByKey` for each
  // plan below.
  const UMBRELLA_INHERIT = {
    everything_starter: "starter",
    everything_free: "free",
  };
  const UMBRELLA_KEYS = new Set(Object.keys(UMBRELLA_INHERIT));

  const { canonicalKeys, labelForKey } = useMemo(() => {
    const tierOrder = ["free", "starter", "pro"];
    const seen = new Set();
    const keys = [];
    const bestLabel = {};
    const sortedPlans = [...plans].sort(
      (a, b) => (tierOrder.indexOf(a.id) + 99) - (tierOrder.indexOf(b.id) + 99),
    );
    // First pass — collect every unique key + pick the most descriptive label.
    sortedPlans.forEach((p) => {
      (p.features || []).forEach((f) => {
        const k = f.key || f.label;
        if (UMBRELLA_KEYS.has(k)) return;
        if (!seen.has(k)) {
          seen.add(k);
          keys.push(k);
          bestLabel[k] = f.label;
        } else if ((f.label || "").length > (bestLabel[k] || "").length) {
          bestLabel[k] = f.label;
        }
      });
    });

    // Second pass — figure out the LOWEST tier where each key is genuinely
    // included (not coming-soon). Coming-soon features fall to the bottom.
    const tierRank = { free: 0, starter: 1, pro: 2, __coming: 3, __unknown: 4 };
    const lowestTier = {};
    const isComingSoon = {};
    sortedPlans.forEach((p) => {
      (p.features || []).forEach((f) => {
        const k = f.key || f.label;
        if (UMBRELLA_KEYS.has(k)) return;
        if (f.coming_soon) isComingSoon[k] = true;
        if (f.included && !f.coming_soon && !(k in lowestTier)) {
          lowestTier[k] = p.id;
        }
      });
    });

    // Sort so that Free features come first, then Starter, then Pro, then
    // Coming Soon. Within a tier we preserve the original authoring order
    // (stable sort via original index).
    const originalIndex = new Map(keys.map((k, i) => [k, i]));
    keys.sort((a, b) => {
      const rankA = tierRank[lowestTier[a] || (isComingSoon[a] ? "__coming" : "__unknown")];
      const rankB = tierRank[lowestTier[b] || (isComingSoon[b] ? "__coming" : "__unknown")];
      if (rankA !== rankB) return rankA - rankB;
      return originalIndex.get(a) - originalIndex.get(b);
    });

    return { canonicalKeys: keys, labelForKey: bestLabel };
  }, [plans]);

  // For each canonical key, find the LOWEST tier that includes it. Umbrella
  // keys make higher tiers inherit ALL of the lower tier's included features,
  // so a Pro plan with `everything_starter: true` is treated as if it also
  // included every Starter feature.
  const unlockedIn = useMemo(() => {
    const tierOrder = ["free", "starter", "pro"];
    const map = {};
    tierOrder.forEach((tid) => {
      const p = plans.find((pp) => pp.id === tid);
      if (!p) return;
      p.features.forEach((f) => {
        const k = f.key || f.label;
        if (f.included && !f.coming_soon && !map[k]) map[k] = tid;
      });
      // Umbrella inheritance — if this plan says "everything in X", stamp
      // all of X's included features as also unlocked here.
      p.features.forEach((umbrella) => {
        const uk = umbrella.key || umbrella.label;
        if (umbrella.included && UMBRELLA_KEYS.has(uk)) {
          const inheritFrom = UMBRELLA_INHERIT[uk];
          const inheritPlan = plans.find((pp) => pp.id === inheritFrom);
          if (!inheritPlan) return;
          inheritPlan.features.forEach((f) => {
            const k = f.key || f.label;
            if (f.included && !f.coming_soon && !map[k]) map[k] = tid;
          });
        }
      });
    });
    return map;
  }, [plans]);

  // Per-plan feature row source (canonical union for free-with-no-header,
  // own features for tiers with a section header, own included-only for
  // the Free tier). Memoized so the cap logic below can reference the
  // same computed values as the render.
  const rowSourcePerPlan = useMemo(() => {
    const map = {};
    plans.forEach((plan) => {
      const hasHeader = (plan.features || []).some((f) => f.section_header);
      const isFreeTier = plan.is_free || plan.id === "free";
      map[plan.id] = hasHeader
        ? (plan.features || []).map((f) => f.key || f.label)
        : isFreeTier
          ? (plan.features || [])
              .filter((f) => f.included && !f.coming_soon)
              .map((f) => f.key || f.label)
          : canonicalKeys;
    });
    return map;
  }, [plans, canonicalKeys]);

  // Cap the tallest tier(s) to the row count of the tallest shorter sibling
  // so all cards render at the same height. Only tiers ABOVE the shortest
  // get capped — Free and Starter stay untouched per the user's request.
  // A "See all features" toggle reveals the hidden rows on demand.
  const rowCapPerPlan = useMemo(() => {
    const tierOrder = ["free", "starter", "pro"];
    const caps = {};
    plans.forEach((plan) => {
      const idx = tierOrder.indexOf(plan.id);
      // Only cap tiers strictly above Starter (i.e., Pro and any custom
      // higher tier). Free and Starter always render their full list.
      if (idx < 2) { caps[plan.id] = null; return; }
      let maxLower = 0;
      for (let i = 0; i < idx; i++) {
        const lowerId = tierOrder[i];
        const src = rowSourcePerPlan[lowerId];
        if (src && src.length > maxLower) maxLower = src.length;
      }
      caps[plan.id] = maxLower || null;
    });
    return caps;
  }, [plans, rowSourcePerPlan]);

  // Plan title icon + accent — makes the three tiers visually distinct at a
  // glance instead of relying on text alone.
  const planStyles = {
    free: {
      Icon: Leaf,
      iconWrap: "bg-slate-100 text-slate-500",
      accent: "text-slate-500",
    },
    starter: {
      Icon: Rocket,
      iconWrap: "bg-[#0D9488]/10 text-[#0D9488]",
      accent: "text-[#0D9488]",
    },
    pro: {
      Icon: Crown,
      iconWrap: "bg-amber-100 text-amber-600",
      accent: "text-amber-600",
    },
  };

  return (
    <div className="space-y-6">
      {/* Billing cycle toggle */}
      <div className="flex justify-center">
        <div className="inline-flex bg-slate-100 rounded-full p-1" role="tablist" data-testid="billing-cycle-toggle">
          <button
            type="button"
            onClick={() => setBillingCycle("monthly")}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              !isAnnual ? "bg-white shadow-sm text-[#0A192F]" : "text-slate-500 hover:text-[#0A192F]"
            }`}
            data-testid="billing-monthly"
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setBillingCycle("annual")}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
              isAnnual ? "bg-white shadow-sm text-[#0A192F]" : "text-slate-500 hover:text-[#0A192F]"
            }`}
            data-testid="billing-annual"
          >
            Annual
            <span className="bg-[#10B981] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">SAVE 20%</span>
          </button>
        </div>
      </div>

      {/* Discount code — single input above the cards. When a code applies
          only to a specific plan we automatically preview the discounted
          price on THAT card and forward the code to Stripe checkout. */}
      <DiscountCodeInput
        billingCycle={billingCycle}
        codeInput={codeInput}
        setCodeInput={setCodeInput}
        codeValidating={codeValidating}
        setCodeValidating={setCodeValidating}
        codeError={codeError}
        setCodeError={setCodeError}
        appliedCode={appliedCode}
        setAppliedCode={setAppliedCode}
        plans={plans}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="pricing-grid">
        {isLoading && plans === PLANS && (
          <div className="col-span-full text-center text-slate-400 text-sm">Loading plans…</div>
        )}
        {plans.map(plan => {
          const isCurrent = currentPlan === plan.id;
          const planRank = { free: 0, starter: 1, pro: 2 };
          const isUpgrade = currentPlan && (planRank[plan.id] ?? 99) > (planRank[currentPlan] ?? 0);
          // Prefer the real annual price from the CMS; fall back to 20%-off
          // heuristic only when the plan came from the static PLANS array.
          const monthlyEquiv = isAnnual
            ? (plan.annualPrice ? Math.round(plan.annualPrice / 12) : Math.round(plan.price * 0.8))
            : plan.price;
          const annualTotal = isAnnual
            ? (plan.annualPrice || Math.round(plan.price * 12 * 0.8))
            : null;
          const annualSavings = isAnnual && plan.price > 0
            ? (plan.price * 12) - annualTotal
            : 0;

          // If an applied discount code is compatible with THIS plan +
          // cycle, compute the effective per-month price so the card
          // previews what the customer will pay at checkout.
          const codeAppliesToPlan = appliedCode
            && (!appliedCode.applicable_plans?.length || appliedCode.applicable_plans.includes(plan.id))
            && (appliedCode.billing_cycle === "any" || appliedCode.billing_cycle === "" || appliedCode.billing_cycle === billingCycle);
          let discountedMonthlyEquiv = null;
          if (codeAppliesToPlan && plan.price > 0) {
            const baseMonthly = monthlyEquiv;
            if (appliedCode.discount_type === "percent") {
              discountedMonthlyEquiv = Math.max(0, Math.round(baseMonthly * (100 - appliedCode.discount_value) / 100));
            } else {
              // amount off in cents — normalize to monthly dollars for display
              const offMonthly = isAnnual ? Math.round((appliedCode.discount_value / 12) / 100) : Math.round(appliedCode.discount_value / 100);
              discountedMonthlyEquiv = Math.max(0, baseMonthly - offMonthly);
            }
          }

          // Look up each canonical feature's status in *this* plan (keyed
          // by feature `key`, not label, so re-worded variants match). If
          // the plan declares an umbrella like `everything_starter`, we
          // inherit all of Starter's rows so the Pro card shows the same
          // features as included (with a check), not as excluded.
          const featuresByKey = {};
          (plan.features || []).forEach((f) => {
            const k = f.key || f.label;
            featuresByKey[k] = f;
          });
          (plan.features || []).forEach((umbrella) => {
            const uk = umbrella.key || umbrella.label;
            if (umbrella.included && UMBRELLA_INHERIT[uk]) {
              const inheritPlan = plans.find((pp) => pp.id === UMBRELLA_INHERIT[uk]);
              if (inheritPlan) {
                inheritPlan.features.forEach((f) => {
                  const k = f.key || f.label;
                  if (!featuresByKey[k] && f.included) {
                    featuresByKey[k] = { ...f, included: true };
                  }
                });
              }
            }
          });

          const style = planStyles[plan.id] || planStyles.free;
          const TitleIcon = style.Icon;

          return (
            <div
              key={plan.id}
              className={`relative rounded-xl border p-6 flex flex-col ${
                plan.popular ? "border-[#0D9488] shadow-lg shadow-teal-100/40" : "border-slate-200"
              } ${isCurrent ? "ring-2 ring-amber-400" : ""}`}
              data-testid={`pricing-card-${plan.id}`}
            >
              {plan.popular && !isCurrent && (
                <Badge className="absolute -top-3 left-6 bg-[#0D9488] text-white">Most Popular</Badge>
              )}
              {isCurrent && (
                <Badge className="absolute -top-3 left-6 bg-amber-400 text-amber-900">Current Plan</Badge>
              )}
              <div className="space-y-2 mb-5">
                <div className="flex items-center gap-2">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${style.iconWrap}`}>
                    <TitleIcon className="w-5 h-5" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0A192F]">{plan.name}</h3>
                </div>
                <div className="flex items-baseline gap-1">
                  {discountedMonthlyEquiv !== null ? (
                    <>
                      <span className="text-4xl font-black tracking-tight text-[#0A192F]" data-testid={`discounted-price-${plan.id}`}>${discountedMonthlyEquiv}</span>
                      <span className="text-sm text-slate-500">/month</span>
                      <span className="ml-2 text-base text-slate-400 line-through">${monthlyEquiv}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-black tracking-tight text-[#0A192F]">${monthlyEquiv}</span>
                      <span className="text-sm text-slate-500">/month</span>
                    </>
                  )}
                </div>
                {discountedMonthlyEquiv !== null && (
                  <p className="text-xs text-[#0D9488] font-semibold" data-testid={`discount-applied-${plan.id}`}>
                    Code {appliedCode.code} applied
                  </p>
                )}
                {isAnnual && plan.price > 0 ? (
                  <p className="text-xs text-[#10B981] font-medium" data-testid={`annual-billed-${plan.id}`}>
                    Billed ${annualTotal} annually{annualSavings > 0 && ` · You save $${annualSavings}/yr`}
                  </p>
                ) : (
                  <p className="text-sm text-slate-500">{plan.description}</p>
                )}
              </div>
              <ul className="space-y-2 flex-1 mb-4" data-testid={`feature-list-${plan.id}`}>
                {(() => {
                  const rowSource = rowSourcePerPlan[plan.id] || [];
                  const cap = rowCapPerPlan[plan.id];
                  const isExpanded = expandedPlans.has(plan.id);
                  return (cap && !isExpanded) ? rowSource.slice(0, cap) : rowSource;
                })().map((k) => {
                  const label = labelForKey[k] || (featuresByKey[k]?.label) || k;
                  const f = featuresByKey[k] || { label, included: false, coming_soon: false };
                  const unlockTier = !f.included && !f.coming_soon ? unlockedIn[k] : null;
                  const showUpgradePill = !!unlockTier && unlockTier !== plan.id;
                  // "Everything in X, plus:" header — bold, no icon, no
                  // pill. Reads like a section title inside the list.
                  if (f.section_header) {
                    return (
                      <li key={k} className="pt-2 pb-1" data-testid={`section-header-${plan.id}`}>
                        <span className="text-[13px] font-semibold text-slate-800">{label}</span>
                      </li>
                    );
                  }
                  return (
                    <li
                      key={k}
                      className="flex items-start gap-2 text-sm"
                      data-testid={f.coming_soon ? `feature-coming-soon-${plan.id}` : undefined}
                    >
                      {f.coming_soon ? (
                        <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      ) : f.included ? (
                        <Check className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5" />
                      )}
                      <span className="flex-1">
                        <span
                          className={
                            f.coming_soon
                              ? "text-slate-700"
                              : f.included
                              ? "text-slate-700"
                              : "text-slate-400"
                          }
                        >
                          {label}
                        </span>
                        {f.coming_soon && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase align-middle">
                            Coming Soon
                          </span>
                        )}
                        {showUpgradePill && !f.coming_soon && (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className={`ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full align-middle cursor-help ${
                                    unlockTier === "pro"
                                      ? "bg-amber-100 text-amber-600 hover:bg-amber-200"
                                      : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                                  }`}
                                  data-testid={`unlock-tier-pill-${plan.id}`}
                                  aria-label={`Available in ${unlockTier === "pro" ? "Pro" : "Starter"}`}
                                >
                                  {unlockTier === "pro" ? (
                                    <Crown className="w-3 h-3" />
                                  ) : (
                                    <Rocket className="w-3 h-3" />
                                  )}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="bg-slate-900 text-white">
                                Available in {unlockTier === "pro" ? "Pro" : "Starter"}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {f.note && (
                          <span className="block text-xs text-slate-500 italic mt-0.5">{f.note}</span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {(() => {
                const rowSource = rowSourcePerPlan[plan.id] || [];
                const cap = rowCapPerPlan[plan.id];
                if (!cap || rowSource.length <= cap) return null;
                const isExpanded = expandedPlans.has(plan.id);
                const hiddenCount = rowSource.length - cap;
                return (
                  <button
                    type="button"
                    onClick={() => togglePlanExpanded(plan.id)}
                    className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-[#0D9488] hover:text-[#0F766E] self-start"
                    data-testid={`expand-features-${plan.id}`}
                    aria-expanded={isExpanded}
                  >
                    {isExpanded ? (
                      <>Show less <ChevronUp className="w-4 h-4" /></>
                    ) : (
                      <>See all features (+{hiddenCount} more) <ChevronDown className="w-4 h-4" /></>
                    )}
                  </button>
                );
              })()}
              {plan.is_free || plan.id === "free" ? (
                isCurrent ? (
                  <Button variant="outline" size={ctaSize} disabled className="w-full" data-testid={`current-plan-${plan.id}`}>
                    Current Plan
                  </Button>
                ) : currentPlan && currentPlan !== "free" ? (
                  <DowngradeButton plan="free" size={ctaSize} className="w-full" />
                ) : (
                  <Button
                    variant="outline"
                    size={ctaSize}
                    onClick={onSelectFree}
                    className="w-full"
                    data-testid={`select-${plan.id}-btn`}
                  >
                    {onSelectFree ? "Get Started Free" : "Free Forever"}
                  </Button>
                )
              ) : isCurrent ? (
                <Button variant="outline" size={ctaSize} disabled className="w-full" data-testid={`current-plan-${plan.id}`}>
                  Current Plan
                </Button>
              ) : isUpgrade || !currentPlan ? (
                <UpgradeButton
                  plan={plan.id}
                  billingCycle={billingCycle}
                  size={ctaSize}
                  compact
                  discountCode={codeAppliesToPlan ? appliedCode.code : ""}
                  className={`w-full ${plan.popular ? "" : "bg-[#0A192F] hover:bg-[#112240]"}`}
                />
              ) : (
                <DowngradeButton plan={plan.id} size={ctaSize} className="w-full" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};


// -----------------------------------------------------------------------------
// DiscountCodeInput — small inline UI used above the pricing cards. Validates
// the entered code against /api/discount-codes/validate. On success the
// parent PricingGrid uses `appliedCode` to preview the discounted price on
// applicable cards and forwards the code to /api/subscription/checkout.
// -----------------------------------------------------------------------------
const DiscountCodeInput = ({
  billingCycle,
  codeInput,
  setCodeInput,
  codeValidating,
  setCodeValidating,
  codeError,
  setCodeError,
  appliedCode,
  setAppliedCode,
  plans,
}) => {
  const [expanded, setExpanded] = useState(false);

  const apply = async () => {
    const trimmed = codeInput.trim().toUpperCase();
    if (!trimmed) return;
    setCodeError("");
    setCodeValidating(true);
    // Validate against the plans that are actually paid (skip free) — we try
    // each one in tier order so a code marked applicable_plans=["starter"]
    // succeeds on the Starter plan even if the customer's currently on Free.
    const targetPlans = plans
      .filter((p) => p.price > 0 || !p.is_free)
      .map((p) => p.id);
    let lastError = "Invalid or expired code";
    let successData = null;
    for (const pid of targetPlans) {
      try {
        const res = await apiClient.post("/discount-codes/validate", {
          code: trimmed,
          plan_id: pid,
          billing_cycle: billingCycle,
        });
        successData = res.data;
        break;
      } catch (e) {
        lastError = e.response?.data?.detail || lastError;
      }
    }
    setCodeValidating(false);
    if (!successData) {
      setCodeError(lastError);
      setAppliedCode(null);
      return;
    }
    // Look up the full doc so we know applicable_plans / billing_cycle
    // constraints for the card price preview. We derived these from the
    // validate response fields + reused plan lookup.
    setAppliedCode({
      code: successData.code,
      discount_type: successData.discount_type,
      discount_value: successData.discount_value,
      duration_type: successData.duration_type,
      duration_periods: successData.duration_periods,
      applicable_plans: successData.applicable_plans || [],
      billing_cycle: successData.billing_cycle_restriction || "any",
      savings_cents: successData.savings_cents,
    });
    setCodeError("");
    toast.success(`Code ${successData.code} applied!`);
  };

  const remove = () => {
    setAppliedCode(null);
    setCodeInput("");
    setCodeError("");
  };

  if (appliedCode) {
    return (
      <div className="flex justify-center">
        <div
          className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm"
          data-testid="applied-discount-chip"
        >
          <Sparkles className="w-4 h-4 text-emerald-600" />
          <span className="text-emerald-800 font-medium">
            Discount <span className="font-mono font-bold">{appliedCode.code}</span> applied
          </span>
          <button
            type="button"
            onClick={remove}
            className="ml-1 text-emerald-700 hover:text-emerald-900 text-xs underline underline-offset-2"
            data-testid="remove-discount-btn"
          >
            Remove
          </button>
        </div>
      </div>
    );
  }

  if (!expanded) {
    return (
      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-sm text-slate-500 hover:text-[#0D9488] flex items-center gap-1.5"
          data-testid="show-discount-input-btn"
        >
          <Sparkles className="w-3.5 h-3.5" />
          Have a discount code?
        </button>
      </div>
    );
  }

  return (
    <div className="flex justify-center">
      <div className="flex flex-col gap-2 items-center max-w-md w-full">
        <div className="flex gap-2 w-full">
          <input
            type="text"
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && apply()}
            placeholder="Enter code"
            className="flex-1 h-10 px-3 rounded-md border border-slate-300 focus:border-[#0D9488] focus:ring-1 focus:ring-[#0D9488] outline-none text-sm font-mono tracking-wider uppercase"
            data-testid="discount-code-input"
            autoFocus
          />
          <Button
            onClick={apply}
            disabled={codeValidating || !codeInput.trim()}
            className="bg-[#0D9488] hover:bg-[#14B8A6] h-10"
            data-testid="apply-discount-btn"
          >
            {codeValidating ? "Checking…" : "Apply"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => { setExpanded(false); setCodeInput(""); setCodeError(""); }}
            className="h-10"
            data-testid="cancel-discount-btn"
          >
            Cancel
          </Button>
        </div>
        {codeError && (
          <p className="text-xs text-red-600 flex items-center gap-1" data-testid="discount-error">
            <X className="w-3 h-3" /> {codeError}
          </p>
        )}
      </div>
    </div>
  );
};
